import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Code, Mail, ImageIcon } from "lucide-react";
import DOMPurify from "dompurify";
import SanitizedHtml from "@/components/customer/ticket/SanitizedMessage";
import {
  BARE_IMAGE_LINK_REGEX,
  processContent,
  type MessageContentData,
} from "./ticketHelpers";

// TODO: fix processed content caching
export default function MessageContent({
  emailIsForwarded,
  emailHasForwarderContent,
  content,
  showFullContent,
  onToggleContent,
  loadRemoteImages,
  onToggleRemoteImages,
  hideFullContentToggle = false,
}: {
  emailIsForwarded: boolean;
  emailHasForwarderContent: boolean;
  content: MessageContentData;
  showFullContent: boolean;
  onToggleContent: () => void;
  loadRemoteImages: boolean;
  onToggleRemoteImages: () => void;
  hideFullContentToggle?: boolean;
}) {
  const processedFullHtml = useMemo(
    () =>
      content.emailFullHtml
        ? processContent(content.emailFullHtml, true, loadRemoteImages)
        : null,
    [content.emailFullHtml, loadRemoteImages],
  );

  const processedFullText = useMemo(
    () =>
      content.emailFullText
        ? processContent(content.emailFullText, false, loadRemoteImages)
        : null,
    [content.emailFullText, loadRemoteImages],
  );

  const processedForwardedText = useMemo(
    () =>
      content.emailForwardedContentText
        ? processContent(
            content.emailForwardedContentText,
            false,
            loadRemoteImages,
          )
        : null,
    [content.emailForwardedContentText, loadRemoteImages],
  );

  const processedText = useMemo(
    () =>
      content.text
        ? processContent(content.text, false, loadRemoteImages)
        : null,
    [content.text, loadRemoteImages],
  );

  const processedHtml = useMemo(
    () =>
      content.html
        ? processContent(content.html, true, loadRemoteImages)
        : null,
    [content.html, loadRemoteImages],
  );

  // Only show toggle button if there's full content available
  const hasFullContent = Boolean(
    content.emailFullHtml ?? content.emailFullText,
  );

  // Add new function to check for external images
  const hasExternalImages = useMemo(() => {
    const contentToCheck = showFullContent
      ? (content.emailFullHtml ?? content.emailFullText)
      : emailIsForwarded && !emailHasForwarderContent
        ? content.emailForwardedContentText
        : (content.html ?? content.text);

    if (!contentToCheck) return false;

    // Check for HTML img tags with src that isn't a data URL
    const imgRegex = /<img[^>]+src=["'](?!data:)[^"']+["'][^>]*>/i;
    // Plain-text parts carry images as a bracketed URL instead of a tag
    return (
      imgRegex.test(contentToCheck) ||
      new RegExp(BARE_IMAGE_LINK_REGEX.source, "i").test(contentToCheck)
    );
  }, [content, showFullContent, emailIsForwarded, emailHasForwarderContent]);

  return (
    <>
      <div className="flex gap-2 mb-2">
        {hasFullContent && !hideFullContentToggle && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleContent}
            className="rounded-sm"
          >
            {showFullContent ? (
              <>
                <Code className="w-4 h-4 mr-2" />
                Kompakte Ansicht
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Gesamte Nachricht anzeigen
              </>
            )}
          </Button>
        )}
        {!loadRemoteImages && hasExternalImages && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleRemoteImages}
            className="rounded-sm"
          >
            <ImageIcon className="w-4 h-4 mr-2" />
            Externe Bilder laden
          </Button>
        )}
      </div>

      {showFullContent ? (
        processedFullHtml ? (
          <SanitizedHtml htmlContent={processedFullHtml} />
        ) : processedFullText ? (
          <div
            className="text-sm break-all [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a]:underline"
            // Sanitized via DOMPurify with a restrictive tag/attr allowlist below.
            // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(processedFullText, {
                ADD_TAGS: ["a"],
                ADD_ATTR: ["href", "target", "rel"],
              }),
            }}
          />
        ) : (
          <p className="text-sm text-muted-foreground italic">
            Keine Nachrichteninhalte gefunden.
          </p>
        )
      ) : emailIsForwarded &&
        !emailHasForwarderContent &&
        processedForwardedText ? (
        <div
          className="text-sm break-all [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a]:underline"
          // Sanitized via DOMPurify with a restrictive tag/attr allowlist below.
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(processedForwardedText, {
              ADD_TAGS: ["a"],
              ADD_ATTR: ["href", "target", "rel"],
            }),
          }}
        />
      ) : processedText ? (
        <div
          className="text-sm break-all [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a]:underline"
          // Sanitized via DOMPurify with a restrictive tag/attr allowlist below.
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(processedText, {
              ADD_TAGS: ["a"],
              ADD_ATTR: ["href", "target", "rel"],
            }),
          }}
        />
      ) : processedHtml ? (
        <SanitizedHtml htmlContent={processedHtml} />
      ) : (
        <p className="text-sm text-muted-foreground italic">
          Keine Nachrichteninhalte gefunden.
        </p>
      )}
    </>
  );
}
